import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, Target, CheckCircle, Building2, Beaker, DollarSign, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Analysis() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [deck, setDeck] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        const deckRes = await fetch(`${BACKEND_URL}/api/decks/${deckId}`, { credentials: "include" });
        const matchesRes = await fetch(`${BACKEND_URL}/api/decks/${deckId}/matches`, { credentials: "include" });
        if (userRes.ok) setUser(await userRes.json());
        if (deckRes.ok) setDeck(await deckRes.json());
        if (matchesRes.ok) {
          const data = await matchesRes.json();
          setMatches(data.matches || []);
        }
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [deckId]);

  const runMatching = async () => {
    setMatching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/decks/${deckId}/match`, {
        method: "POST", credentials: "include"
      });
      if (!response.ok) throw new Error("Matching failed");
      const data = await response.json();
      setMatches(data.matches || []);
      toast.success("Matches found!");
    } catch (error) {
      toast.error("Failed to match");
    } finally {
      setMatching(false);
    }
  };

  const runAnalysis = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/decks/${deckId}/analyze`, {
        method: "POST", credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setDeck(prev => ({ ...prev, analysis: data.analysis }));
        toast.success("Analysis complete!");
      }
    } catch (error) {
      toast.error("Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;
  }

  if (!deck) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 py-12">
          <Card className="p-12 text-center">
            <p className="text-slate-500">Deck not found</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-4">Back</Button>
          </Card>
        </main>
      </div>
    );
  }

  const analysis = deck.analysis || {};
  const scoreColor = (s) => s >= 70 ? "text-teal-600 bg-teal-50" : s >= 50 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{deck.filename}</h1>
            <p className="text-slate-500">Uploaded {new Date(deck.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        <Tabs defaultValue="analysis" className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-full">
            <TabsTrigger value="analysis" className="rounded-full px-6">Analysis</TabsTrigger>
            <TabsTrigger value="matches" className="rounded-full px-6">Matches ({matches.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="analysis" className="space-y-6">
            {!analysis.company_name ? (
              <Card className="p-12 text-center border border-slate-200">
                <Sparkles className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Deck Not Analyzed Yet</h3>
                <p className="text-slate-500 mb-6">Run AI analysis to extract info</p>
                <Button onClick={runAnalysis} className="btn-secondary" data-testid="run-analysis-btn">Run AI Analysis</Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <Card className="border border-slate-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-teal-600" />Company Overview</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <h3 className="text-2xl font-bold">{analysis.company_name}</h3>
                      <p className="text-slate-600">{analysis.summary}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-teal-50 text-teal-700">{analysis.stage}</Badge>
                        <Badge className="bg-slate-100">{analysis.technology_type}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <Card className="border border-slate-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <Beaker className="w-5 h-5 text-teal-600" />
                      <div><p className="text-sm text-slate-500">Focus Area</p><p className="font-semibold">{analysis.focus_area}</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5" />
                      <div><p className="text-sm text-slate-500">Funding Ask</p><p className="font-semibold">{analysis.funding_ask || "N/A"}</p></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="matches" className="space-y-6">
            <Card className="border border-slate-200 bg-gradient-to-r from-slate-50 to-teal-50/30">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{matches.length > 0 ? `${matches.length} Matches` : "Find Matches"}</h3>
                  <p className="text-sm text-slate-500 mt-1">AI-powered investor matching</p>
                </div>
                <Button onClick={runMatching} disabled={matching || !analysis.company_name} className="btn-secondary" data-testid="match-investors-btn">
                  {matching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                  {matches.length > 0 ? "Re-run" : "Find"}
                </Button>
              </CardContent>
            </Card>
            {matches.length > 0 ? (
              <div className="grid gap-4">
                {matches.map((m) => (
                  <Card key={m.match_id} className="hover-lift cursor-pointer border border-slate-200" onClick={() => navigate(`/match/${m.match_id}`)}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold">{m.investor?.name} <span className="text-slate-400">•</span> <span className="text-slate-600 font-normal">{m.investor?.firm}</span></h3>
                          <p className="text-slate-600 line-clamp-2 my-2">{m.rationale}</p>
                        </div>
                        <div className={`px-4 py-2 rounded-full font-mono font-bold ${scoreColor(m.match_score)}`}>{m.match_score}%</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border border-slate-200 p-12 text-center">
                <Target className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No Matches Yet</h3>
                <p className="text-slate-500">{analysis.company_name ? "Click Find Matches" : "Analyze first"}</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
